import React, { useCallback, useEffect, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { Paper, Stack, Tooltip, Typography } from '@mui/material';

import { TemplateOption_TemplateFragment } from 'src/generated/mloop-graphql';

type Fragments = {
  template: TemplateOption_TemplateFragment;
};

/**
 * Dropdown row for a `Template` of any `TemplateType` — email, calendar invite, Slack message —
 * that pops a preview card (name, subject, body) next to the row on hover.
 */
const TemplateOption: FCWithFragments<Fragments> = ({ template }) => {
  const { t } = useTranslation();
  const [anchorOriginPlacement, setAnchorOriginPlacement] = useState<'left' | 'right'>('right');

  const anchorEl = useRef<HTMLDivElement | null>(null);

  // We want to check if there is enough room to place the popover to the right of the anchor element
  // This checks if the distance from the right edge of the window to the right edge of the anchor element is greater than 350px
  const compareSize = useCallback(() => {
    if (!anchorEl.current) {
      return;
    }

    const isWideEnough = window.innerWidth - anchorEl.current.getBoundingClientRect().right > 350;
    if (isWideEnough) {
      setAnchorOriginPlacement('right');
    } else {
      setAnchorOriginPlacement('left');
    }
  }, [anchorEl]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect manages an external subscription/listener after render and cleanup.
  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);

    return () => {
      window.removeEventListener('resize', compareSize);
    };
  }, [compareSize]);

  return (
    <Tooltip
      title={
        // The test id has to live on the card itself, not on the `Tooltip`: MUI clones unknown
        // props onto the trigger child, so a `data-testid` on `Tooltip` marks the dropdown row
        // (which is always mounted) instead of the popover.
        <Paper variant="outlined" data-testid={`template-preview-popover-${template.id}`}>
          <Stack spacing={1} p={1} justifyContent="center" alignItems="center">
            {/* ENG-26352: the preview card's whole point is letting a customer read the FULL
                template title — the dropdown row already truncates it — so the heading is the
                name itself and wraps instead of eliding. Falls back to the generic label when
                the template has no name so the heading is never blank. */}
            <Typography variant="subtitle1" fontWeight={700} sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {template.name || t('hiring-dashboard-common:common.template.preview')}
            </Typography>
            <Paper
              sx={{
                backgroundColor: (theme) => theme.palette.background.contrast,
                maxHeight: '60vh',
                overflowY: 'auto',
                width: '100%',
              }}
            >
              <Stack p={1}>
                <Typography variant="subtitle2">{t('hiring-dashboard-common:common.template.subject')}</Typography>
                <Typography>{template.subject}</Typography>
                <Typography variant="subtitle2">{t('hiring-dashboard-common:common.template.body')}</Typography>
                <Typography>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: template.body || '' }} />
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Paper>
      }
      placement={anchorOriginPlacement}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
            maxWidth: 'unset',
            width: '400px',
          },
        },
      }}
    >
      <Typography sx={{ width: '100%' }} noWrap>
        {template.name}
      </Typography>
    </Tooltip>
  );
};

TemplateOption.fragments = {
  template: gql`
    fragment TemplateOption_Template on Template {
      id
      type
      name
      subject
      body
    }
  `,
};

export default TemplateOption;
